import I18n from 'i18n-js'
import React, { useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import FontAwesome from 'react-fontawesome'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import UserApi from 'services/userApi'
import PatrimonyApi from '../../../services/patrimonyApi'
import { CONSTANTS } from '../../../utils/constants'
import { errorToast, infoToast, successToast, userSyncWarningToast } from '../../../utils/toast'
import EstateUserFilters from '../../filters/EstateUserFilters'
import Loader from '../../loader/Loader'
import Pagination from '../../pagination/Pagination'
import EstateUsersEditionTableRow from './EstateUsersEditionTableRow'
import ModalEditAddress from './ModalEditAddress'
import ModalImportCsv from './ModalImportCsv'
import ModalImportResults from './ModalImportResults'

const EstateUsersEditionTable = () => {
  const { groupId } = useParams()
  const error = useSelector((state) => state.error) // assuming there's a global error state

  const [users, setUsers] = useState([])
  const [agencies, setAgencies] = useState([])
  const [managers, setManagers] = useState([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(undefined)
  const [pageCount, setPageCount] = useState(undefined)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const [loading, setLoading] = useState(true)
  const [showModalCsv, setShowModalCsv] = useState(false)
  const [sortBy, setSortBy] = useState(null)
  const [filterList, setFilterList] = useState({})
  const [showModalAddress, setShowModalAddress] = useState(false)
  const [selectedUserAddress, setSelectedUserAddress] = useState(null)
  const [importResults, setImportResults] = useState(null)
  const [showImportResults, setShowImportResults] = useState(false)
  const [savedFile, setSavedFile] = useState(null)

  const fetchUsers = (page) => {
    setLoading(true)
    UserApi.getUsersList({
      ...filterList,
      groupId,
      currentPage: page || currentPage,
      searchValue: debouncedSearchValue,
      sortBy,
    })
      .then((users) => {
        setUsers(users.userList)
        setTotalCount(users.metadata.totalCount)
        setPageCount(users.metadata.pageCount)
      })
      .finally(() => setLoading(false))
  }

  const getAgencies = () => {
    PatrimonyApi.getPatrimonyList({ groupId, size: 1000 }).then((result) => {
      const agenciesList = []
      result.records.forEach((patrimony) => {
        patrimony.sectors.forEach((sector) => {
          sector.agencies.forEach((agency) => {
            agenciesList.push(agency)
          })
        })
      })
      setAgencies(agenciesList.filter((a) => a.active))
    })
  }

  const getManagers = () => {
    UserApi.getUsersList({
      groupId,
      role: CONSTANTS.ROLES.MANAGER,
    }).then((result) => {
      setManagers(result)
    })
  }

  const synchronizeCRMUsers = () => {
    setLoading(true)
    infoToast(I18n.t('PendingSync'))
    UserApi.syncUserListWithCRM()
      .then((data) => {
        fetchUsers()
        if (data.syncResult === 'error') {
          errorToast(I18n.t('ErrorSyncCRM'))
          return
        }

        if (data.syncResult === 'warning') {
          userSyncWarningToast({
            ...data,
            message: `${data.errors.length} ${I18n.t('WarningSyncCRM')}`,
          })
          return
        }

        successToast(I18n.t('SuccessSyncCRM'))
      })
      .catch(() => {
        errorToast(I18n.t('ErrorSyncCRM'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const getUserAgencyRelationsExport = () => {
    PatrimonyApi.exportPatrimony().finally(() => {})
  }

  const handleChange = (type, index, items) => {
    let updatedUsers = [...users]
    updatedUsers[index][type] = items
    setUsers(updatedUsers)
  }

  const handlePagination = (pageToShow) => {
    setCurrentPage(pageToShow)
  }

  const onSearchValueChange = (event) => {
    setSearchValue(event.target.value)
  }

  const toggleModalImportCsv = () => {
    setShowModalCsv(!showModalCsv)
  }

  // Debounce searchValue pour éviter trop de requêtes pendant la frappe
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    fetchUsers()
  }, [debouncedSearchValue, filterList, currentPage])

  const handleFilterChange = (filterKey, filterValue) => {
    let updatedFilterList = { ...filterList }
    switch (filterKey) {
      case 'hasNoAgencies':
        if (filterValue) {
          updatedFilterList['agency'] = ''
        }
        break
      case 'agency':
        if (filterValue.length > 0) {
          updatedFilterList['hasNoAgencies'] = false
        }
        break
      default:
        break
    }
    updatedFilterList[filterKey] = filterValue
    setFilterList(updatedFilterList)
    setCurrentPage(0)
  }

  const importCsv = (item, isDryRun) => {
    setLoading(true)
    setSavedFile(item)

    UserApi.importCsvUserRelations(item, { groupId }, isDryRun)
      .then((response) => {
        const adaptedResults = {
          isDryRun,
          totalProcessed: response.totalProcessed || 0,
          successCount: response.successCount || 0,
          errorCount: response.errorCount || 0,
          errors: response.errors ? response.errors.split('\n').filter((line) => line.trim()) : [],
          rawErrors: response.errors || null,
          allDataAlreadyImported: response.allDataAlreadyImported || false,
          shouldReload: !isDryRun && response.successCount > 0,
          created: response.created || {},
          skipped: response.skipped || {},
          stats: response.stats || {},
        }

        // Si c'est un import réel réussi, afficher un toast
        const hasOnlyDuplicateErrors = adaptedResults.errors.every((error) => error.includes('existe déjà') || error.includes('doublon'))
        const isRealImport = !isDryRun
        const hasSuccessfulImports = adaptedResults.successCount > 0

        if (isRealImport && (adaptedResults.errorCount === 0 || (hasOnlyDuplicateErrors && hasSuccessfulImports))) {
          // Afficher un toast de succès pour les imports sans erreurs ou avec seulement des doublons
          const duplicateCount = hasOnlyDuplicateErrors ? adaptedResults.errorCount : 0
          let message = `Import réussi ! ${adaptedResults.successCount} ligne(s) importée(s).`
          if (duplicateCount > 0) {
            message += ` ${duplicateCount} doublon(s) ignoré(s).`
          }
          successToast(message)
          fetchUsers()
        } else {
          setImportResults(adaptedResults)
          setShowImportResults(true)

          if (!isDryRun && adaptedResults.successCount > 0) {
            fetchUsers()
          }
        }
      })
      .catch((error) => {
        console.error('Import error:', error)
        setImportResults({
          isDryRun,
          totalProcessed: 0,
          successCount: 0,
          errorCount: 1,
          errors: [I18n.t('ErrorImport')],
          rawErrors: I18n.t('ErrorImport'),
          allDataAlreadyImported: false,
          shouldReload: false,
          created: { patrimonies: 0, secteurs: 0, agences: 0 },
        })
        setShowImportResults(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const toggleModalAddress = (user = null) => {
    setShowModalAddress(!showModalAddress)
    setSelectedUserAddress(user)
  }

  const handleAddressUpdate = (updatedAddress) => {
    UserApi.updateUserAddress(selectedUserAddress.id, updatedAddress)
      .then(() => {
        successToast(I18n.t('SuccessAddressUpdate'))
        fetchUsers()
        toggleModalAddress()
      })
      .catch(() => {
        errorToast(I18n.t('ErrorAddressUpdate'))
      })
  }

  const sortByNames = () => {
    setSortBy((prevSortBy) => (prevSortBy === 'lastname.DESC' ? 'lastname.ASC' : 'lastname.DESC'))
    fetchUsers()
  }

  useEffect(() => {
    fetchUsers()
    getAgencies()
    getManagers()
  }, [groupId])

  useEffect(() => {
    if (users.length > 0) {
      setLoading(false)
    }
  }, [users])

  return (
    <div className="container-fluid">
      <div>
        <div className="gap-x-2 d-flex justify-content-end">
          <div
            className="action-button small"
            onClick={() => window.open(process.env.REACT_APP_MON_COMPTE_URL, '_blank')}
            title={I18n.t('EstateMonCompteSubtitle')}
          >
            <FontAwesome name="user-plus" />
          </div>
          <div className="action-button small" onClick={synchronizeCRMUsers}>
            <FontAwesome name="sync" />
          </div>
          <div className="action-button small" onClick={toggleModalImportCsv}>
            <FontAwesome name="upload" />
          </div>
          <div className="action-button small" onClick={getUserAgencyRelationsExport}>
            <FontAwesome name="download" />
          </div>
        </div>
        <div className="row user-edition">
          <div className={'col-md-3 filters'}>
            <EstateUserFilters
              filterList={filterList}
              handleChange={handleFilterChange}
              lastnameValue={searchValue}
              handleLastname={onSearchValueChange}
              managers={managers}
            />
          </div>
          <div className="col-md-9">
            <p className="error textcenter"> {error} </p>
            <Table responsive hover>
              <thead>
                <tr key="0">
                  <th>
                    <div className="d-flex align-items-center justify-content-between" onClick={sortByNames}>
                      <span>{I18n.t('UserEditionFullname')}</span>
                      <span className={`${sortBy === 'lastname.DESC' ? 'icon-up' : 'icon-down'} mr-2`} />
                    </div>
                  </th>
                  <th>{I18n.t('UserEditionAgencies')}</th>
                  <th>{I18n.t('UserEditionSupervisors')}</th>
                  <th>{I18n.t('UserEditionProfile')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <EstateUsersEditionTableRow
                    key={user.id}
                    user={user}
                    userList={users}
                    agencies={agencies}
                    managers={managers}
                    handleChange={handleChange}
                    fetchUsers={fetchUsers}
                    fetchManagers={getManagers}
                    onEditAddress={() => toggleModalAddress(user)}
                    index={index}
                  />
                ))}
              </tbody>
            </Table>
            {users.length > 0 && totalCount > 1 && (
              <Pagination onChange={handlePagination} pages={pageCount} currentPage={currentPage} totalCount={totalCount} translationKey={'User'} />
            )}
          </div>
        </div>
      </div>
      {loading && <Loader />}
      <ModalImportCsv
        showModal={showModalCsv}
        toggleModal={toggleModalImportCsv}
        importCsv={importCsv}
        type="userRelations"
        onFileChange={() => setSavedFile(null)}
      />
      <ModalImportResults
        show={showImportResults}
        onClose={() => setShowImportResults(false)}
        results={importResults}
        isDryRun={importResults?.isDryRun}
        importType="userRelations"
        onProceedToImport={() => {
          if (savedFile) {
            setShowImportResults(false)
            importCsv(savedFile, false)
          }
        }}
      />
      <ModalEditAddress
        show={showModalAddress}
        onHide={() => toggleModalAddress()}
        user={selectedUserAddress}
        groupId={groupId}
        onUpdate={handleAddressUpdate}
      />
    </div>
  )
}

export default EstateUsersEditionTable
